import { useState, useEffect } from "react";
import { Globe, CheckCircle, Clock, XCircle, Loader, Mail, User, Calendar, AlertCircle, ArrowLeft, Home, Info, X, Trash2, ChevronDown, ChevronUp } from "lucide-react";
import { getAllCustomDomainRequests, updateCustomDomainRequestStatus, deleteCustomDomainRequest, CustomDomainRequest } from "../../services/customDomainService";
import { useNavigate } from "react-router-dom";

export default function CustomDomainManagement() {
  const navigate = useNavigate();
  const [requests, setRequests] = useState<CustomDomainRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState<CustomDomainRequest | null>(null);
  const [adminNotes, setAdminNotes] = useState("");
  const [rejectionReason, setRejectionReason] = useState("");
  const [updating, setUpdating] = useState(false);
  const [filterStatus, setFilterStatus] = useState<string>("all");
  const [showInfoModal, setShowInfoModal] = useState(false);
  const [expandedRequests, setExpandedRequests] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      setLoading(true);
      const data = await getAllCustomDomainRequests();
      setRequests(data);
    } catch (error) {
      console.error("Error loading requests:", error);
      alert("เกิดข้อผิดพลาดในการโหลดข้อมูล");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateStatus = async (
    requestId: string,
    status: 'pending' | 'processing' | 'completed' | 'rejected'
  ) => {
    try {
      setUpdating(true);
      await updateCustomDomainRequestStatus(
        requestId,
        status,
        adminNotes || undefined,
        status === 'rejected' ? rejectionReason : undefined
      );
      
      await loadRequests();
      setSelectedRequest(null);
      setAdminNotes("");
      setRejectionReason("");
      alert("อัปเดตสถานะสำเร็จ!");
    } catch (error) {
      console.error("Error updating status:", error);
      alert("เกิดข้อผิดพลาดในการอัปเดต");
    } finally {
      setUpdating(false);
    }
  };

  const handleDeleteRequest = async (requestId: string) => {
    if (!confirm("คุณแน่ใจหรือไม่ที่จะลบคำขอนี้? การกระทำนี้ไม่สามารถย้อนกลับได้")) {
      return;
    }

    try {
      setUpdating(true);
      await deleteCustomDomainRequest(requestId);
      await loadRequests();
      alert("ลบคำขอสำเร็จ!");
    } catch (error) {
      console.error("Error deleting request:", error);
      alert("เกิดข้อผิดพลาดในการลบ");
    } finally {
      setUpdating(false);
    }
  };

  const toggleExpand = (requestId: string) => {
    setExpandedRequests(prev => {
      const newSet = new Set(prev);
      if (newSet.has(requestId)) {
        newSet.delete(requestId);
      } else {
        newSet.add(requestId);
      }
      return newSet;
    });
  };

  const getStatusBadge = (status: string) => {
    const badges = {
      pending: { color: "bg-yellow-100 text-yellow-800 border-yellow-200", icon: Clock, label: "รอดำเนินการ" },
      processing: { color: "bg-blue-100 text-blue-800 border-blue-200", icon: Loader, label: "กำลังดำเนินการ" },
      completed: { color: "bg-green-100 text-green-800 border-green-200", icon: CheckCircle, label: "เสร็จสิ้น" },
      rejected: { color: "bg-red-100 text-red-800 border-red-200", icon: XCircle, label: "ปฏิเสธ" },
    };
    
    const badge = badges[status as keyof typeof badges] || badges.pending;
    const Icon = badge.icon;
    
    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold border ${badge.color}`}>
        <Icon className="w-3 h-3" />
        {badge.label}
      </span>
    );
  };

  const filteredRequests = filterStatus === "all" 
    ? requests 
    : requests.filter(r => r.status === filterStatus);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 p-4 md:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-6 border-2 border-blue-100">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
                <Globe className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">จัดการ Custom Domain</h1>
                <p className="text-sm text-gray-600">คำขอจากผู้สอนทั้งหมด</p>
              </div>
              <button
                onClick={() => setShowInfoModal(true)}
                className="ml-2 w-8 h-8 bg-blue-100 hover:bg-blue-200 text-blue-600 rounded-lg flex items-center justify-center transition-all"
                title="คำแนะนำสำหรับ Admin"
              >
                <Info className="w-5 h-5" />
              </button>
            </div>
            
            {/* Navigation Buttons */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg transition-all font-semibold"
              >
                <ArrowLeft className="w-4 h-4" />
                กลับ
              </button>
              <button
                onClick={() => navigate('/admin/course-system')}
                className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-all font-semibold"
              >
                <Home className="w-4 h-4" />
                หน้าแรก
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border-2 border-yellow-200">
            <div className="flex items-center gap-3">
              <Clock className="w-8 h-8 text-yellow-600" />
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {requests.filter(r => r.status === 'pending').length}
                </p>
                <p className="text-xs text-gray-600">รอดำเนินการ</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 border-2 border-blue-200">
            <div className="flex items-center gap-3">
              <Loader className="w-8 h-8 text-blue-600" />
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {requests.filter(r => r.status === 'processing').length}
                </p>
                <p className="text-xs text-gray-600">กำลังดำเนินการ</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 border-2 border-green-200">
            <div className="flex items-center gap-3">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {requests.filter(r => r.status === 'completed').length}
                </p>
                <p className="text-xs text-gray-600">เสร็จสิ้น</p>
              </div>
            </div>
          </div>
          
          <div className="bg-white rounded-xl p-4 border-2 border-red-200">
            <div className="flex items-center gap-3">
              <XCircle className="w-8 h-8 text-red-600" />
              <div>
                <p className="text-2xl font-bold text-gray-900">
                  {requests.filter(r => r.status === 'rejected').length}
                </p>
                <p className="text-xs text-gray-600">ปฏิเสธ</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filter */}
        <div className="bg-white rounded-xl p-4 mb-6 border-2 border-gray-200">
          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setFilterStatus("all")}
              className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                filterStatus === "all"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              ทั้งหมด ({requests.length})
            </button>
            <button
              onClick={() => setFilterStatus("pending")}
              className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                filterStatus === "pending"
                  ? "bg-yellow-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              รอดำเนินการ ({requests.filter(r => r.status === 'pending').length})
            </button>
            <button
              onClick={() => setFilterStatus("processing")}
              className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                filterStatus === "processing"
                  ? "bg-blue-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              กำลังดำเนินการ ({requests.filter(r => r.status === 'processing').length})
            </button>
            <button
              onClick={() => setFilterStatus("completed")}
              className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                filterStatus === "completed"
                  ? "bg-green-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              เสร็จสิ้น ({requests.filter(r => r.status === 'completed').length})
            </button>
            <button
              onClick={() => setFilterStatus("rejected")}
              className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                filterStatus === "rejected"
                  ? "bg-red-600 text-white"
                  : "bg-gray-100 text-gray-700 hover:bg-gray-200"
              }`}
            >
              ปฏิเสธ ({requests.filter(r => r.status === 'rejected').length})
            </button>
          </div>
        </div>

        {/* Requests List */}
        <div className="space-y-4">
          {filteredRequests.length === 0 ? (
            <div className="bg-white rounded-xl p-8 text-center border-2 border-gray-200">
              <Globe className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">ไม่มีคำขอในหมวดนี้</p>
            </div>
          ) : (
            filteredRequests.map((request) => {
              const isExpanded = expandedRequests.has(request.id!);
              
              return (
                <div
                  key={request.id}
                  className="bg-white rounded-xl border-2 border-gray-200 hover:border-blue-300 transition-all overflow-hidden"
                >
                  {/* Header - Always Visible */}
                  <div className="p-4 flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 flex-1">
                      <button
                        onClick={() => toggleExpand(request.id!)}
                        className="w-8 h-8 bg-gray-100 hover:bg-gray-200 rounded-lg flex items-center justify-center transition-all"
                      >
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4 text-gray-600" />
                        ) : (
                          <ChevronDown className="w-4 h-4 text-gray-600" />
                        )}
                      </button>
                      
                      {getStatusBadge(request.status)}
                      
                      <div className="flex-1">
                        <code className="bg-blue-50 px-2 py-1 rounded text-blue-700 font-mono text-sm">
                          {request.customDomain}
                        </code>
                      </div>
                      
                      <span className="text-xs text-gray-500">
                        <Calendar className="w-3 h-3 inline mr-1" />
                        {new Date(request.createdAt).toLocaleDateString('th-TH', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit'
                        })}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => setSelectedRequest(request)}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-semibold text-sm"
                      >
                        จัดการ
                      </button>
                      <button
                        onClick={() => handleDeleteRequest(request.id!)}
                        disabled={updating}
                        className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all font-semibold text-sm disabled:opacity-50"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>

                  {/* Expandable Details */}
                  {isExpanded && (
                    <div className="px-4 pb-4 pt-2 border-t border-gray-200 space-y-2">
                      <div className="flex items-center gap-2">
                        <Globe className="w-4 h-4 text-purple-600" />
                        <span className="font-semibold text-gray-900">Subdomain:</span>
                        <code className="bg-purple-50 px-2 py-1 rounded text-purple-700 font-mono text-sm">
                          {request.subdomain}
                        </code>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <User className="w-4 h-4 text-gray-600" />
                        <span className="font-semibold text-gray-900">ผู้สอน:</span>
                        <span className="text-gray-700">{request.userName}</span>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Mail className="w-4 h-4 text-gray-600" />
                        <span className="font-semibold text-gray-900">Email:</span>
                        <span className="text-gray-700">{request.userEmail}</span>
                      </div>

                      {request.adminNotes && (
                        <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                          <p className="text-xs font-semibold text-blue-900 mb-1">หมายเหตุจาก Admin:</p>
                          <p className="text-sm text-blue-800">{request.adminNotes}</p>
                        </div>
                      )}

                      {request.rejectionReason && (
                        <div className="mt-3 p-3 bg-red-50 rounded-lg border border-red-200">
                          <p className="text-xs font-semibold text-red-900 mb-1">เหตุผลที่ปฏิเสธ:</p>
                          <p className="text-sm text-red-800">{request.rejectionReason}</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>

      {/* Modal */}
      {selectedRequest && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">จัดการคำขอ</h2>
            
            <div className="space-y-4 mb-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Custom Domain
                </label>
                <code className="block bg-gray-100 px-4 py-2 rounded-lg text-gray-900 font-mono">
                  {selectedRequest.customDomain}
                </code>
              </div>
              
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Subdomain
                </label>
                <code className="block bg-gray-100 px-4 py-2 rounded-lg text-gray-900 font-mono">
                  {selectedRequest.subdomain}
                </code>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  หมายเหตุจาก Admin (ไม่บังคับ)
                </label>
                <textarea
                  value={adminNotes}
                  onChange={(e) => setAdminNotes(e.target.value)}
                  placeholder="เพิ่มหมายเหตุ..."
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                  rows={3}
                />
              </div>

              {selectedRequest.status !== 'rejected' && (
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    เหตุผลที่ปฏิเสธ (ถ้าปฏิเสธ)
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    placeholder="ระบุเหตุผล..."
                    className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:outline-none"
                    rows={2}
                  />
                </div>
              )}

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <p className="text-sm text-yellow-900 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  <span>
                    <strong>คำแนะนำ DNS สำหรับผู้สอน:</strong><br/>
                    ให้ผู้สอนตั้งค่า CNAME Record:<br/>
                    <code className="bg-white px-2 py-1 rounded mt-1 inline-block">
                      Type: CNAME | Name: (subdomain) | Value: {selectedRequest.subdomain}
                    </code>
                  </span>
                </p>
              </div>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                onClick={() => handleUpdateStatus(selectedRequest.id!, 'processing')}
                disabled={updating}
                className="flex-1 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all font-semibold disabled:opacity-50"
              >
                {updating ? <Loader className="w-5 h-5 animate-spin mx-auto" /> : 'กำลังดำเนินการ'}
              </button>
              
              <button
                onClick={() => handleUpdateStatus(selectedRequest.id!, 'completed')}
                disabled={updating}
                className="flex-1 px-4 py-3 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all font-semibold disabled:opacity-50"
              >
                {updating ? <Loader className="w-5 h-5 animate-spin mx-auto" /> : 'เสร็จสิ้น'}
              </button>
              
              <button
                onClick={() => {
                  if (!rejectionReason) {
                    alert('กรุณาระบุเหตุผลที่ปฏิเสธ');
                    return;
                  }
                  handleUpdateStatus(selectedRequest.id!, 'rejected');
                }}
                disabled={updating}
                className="flex-1 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-all font-semibold disabled:opacity-50"
              >
                {updating ? <Loader className="w-5 h-5 animate-spin mx-auto" /> : 'ปฏิเสธ'}
              </button>
              
              <button
                onClick={() => {
                  setSelectedRequest(null);
                  setAdminNotes("");
                  setRejectionReason("");
                }}
                disabled={updating}
                className="w-full px-4 py-3 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-all font-semibold disabled:opacity-50"
              >
                ปิด
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Info Modal */}
      {showInfoModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-gradient-to-r from-blue-600 to-purple-600 text-white p-6 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <Info className="w-6 h-6" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">คำแนะนำสำหรับ Admin</h2>
                    <p className="text-sm text-blue-100">วิธีการตั้งค่า Custom Domain</p>
                  </div>
                </div>
                <button
                  onClick={() => setShowInfoModal(false)}
                  className="w-8 h-8 bg-white/20 hover:bg-white/30 rounded-lg flex items-center justify-center transition-all"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Overview */}
              <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4">
                <h3 className="font-bold text-blue-900 mb-2 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  ภาพรวม
                </h3>
                <p className="text-sm text-blue-800">
                  เมื่อผู้สอนส่งคำขอใช้ Custom Domain คุณต้องตั้งค่า DNS Record ใน Cloudflare 
                  เพื่อให้ Domain ของผู้สอนชี้มาที่ระบบ EduMi
                </p>
              </div>

              {/* Step 1 */}
              <div className="space-y-3">
                <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                  <span className="w-7 h-7 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm">1</span>
                  เข้าสู่ Cloudflare Dashboard
                </h3>
                <div className="ml-9 space-y-2 text-sm text-gray-700">
                  <p>• เข้า <a href="https://dash.cloudflare.com" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline font-semibold">Cloudflare Dashboard</a></p>
                  <p>• เลือก Domain: <span className="font-mono bg-gray-100 px-2 py-1 rounded">edumi.co</span></p>
                  <p>• ไปที่เมนู <span className="font-semibold">DNS → Records</span></p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="space-y-3">
                <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                  <span className="w-7 h-7 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm">2</span>
                  เพิ่ม CNAME Record
                </h3>
                <div className="ml-9 space-y-3">
                  <p className="text-sm text-gray-700">กดปุ่ม <span className="font-semibold">Add record</span> และกรอกข้อมูลดังนี้:</p>
                  
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
                    <div>
                      <p className="text-xs font-semibold text-gray-500 mb-1">Type</p>
                      <p className="font-mono bg-white px-3 py-2 rounded border border-gray-300">CNAME</p>
                    </div>
                    
                    <div>
                      <p className="text-xs font-semibold text-gray-500 mb-1">Name (subdomain)</p>
                      <p className="font-mono bg-white px-3 py-2 rounded border border-gray-300">
                        <span className="text-blue-600">www</span> 
                        <span className="text-gray-400 ml-2">(จาก Custom Domain ของผู้สอน เช่น www.nudtrip.com → ใส่ www)</span>
                      </p>
                    </div>
                    
                    <div>
                      <p className="text-xs font-semibold text-gray-500 mb-1">Target</p>
                      <p className="font-mono bg-white px-3 py-2 rounded border border-gray-300">
                        <span className="text-purple-600">[username]</span>.edumi.co
                        <span className="text-gray-400 ml-2">(เช่น moon.edumi.co)</span>
                      </p>
                    </div>
                    
                    <div>
                      <p className="text-xs font-semibold text-gray-500 mb-1">Proxy status</p>
                      <p className="font-mono bg-white px-3 py-2 rounded border border-gray-300">
                        <span className="text-orange-600">Proxied</span> (เปิด Cloud สีส้ม)
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 3 */}
              <div className="space-y-3">
                <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                  <span className="w-7 h-7 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm">3</span>
                  แจ้งผู้สอนให้ตั้งค่า DNS
                </h3>
                <div className="ml-9 space-y-2 text-sm text-gray-700">
                  <p>ส่งคำแนะนำให้ผู้สอนไปตั้งค่า DNS ที่ Domain Provider ของเขา:</p>
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 mt-2">
                    <p className="font-semibold text-yellow-900 mb-2">ผู้สอนต้องเพิ่ม CNAME Record:</p>
                    <div className="font-mono text-xs bg-white p-2 rounded border border-yellow-300">
                      <p>Type: <span className="text-blue-600">CNAME</span></p>
                      <p>Name: <span className="text-blue-600">www</span> (หรือ subdomain ที่ต้องการ)</p>
                      <p>Value: <span className="text-purple-600">[username].edumi.co</span></p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Step 4 */}
              <div className="space-y-3">
                <h3 className="font-bold text-gray-900 text-lg flex items-center gap-2">
                  <span className="w-7 h-7 bg-purple-600 text-white rounded-full flex items-center justify-center text-sm">4</span>
                  ตรวจสอบและอัปเดตสถานะ
                </h3>
                <div className="ml-9 space-y-2 text-sm text-gray-700">
                  <p>• รอ DNS Propagation (5-10 นาที)</p>
                  <p>• ทดสอบเข้า Custom Domain ของผู้สอน</p>
                  <p>• ถ้าใช้งานได้แล้ว → เปลี่ยนสถานะเป็น <span className="font-semibold text-green-600">เสร็จสิ้น</span></p>
                  <p>• ถ้ามีปัญหา → เปลี่ยนสถานะเป็น <span className="font-semibold text-blue-600">กำลังดำเนินการ</span> และเพิ่มหมายเหตุ</p>
                </div>
              </div>

              {/* Important Notes */}
              <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4">
                <h3 className="font-bold text-red-900 mb-2 flex items-center gap-2">
                  <AlertCircle className="w-5 h-5" />
                  ข้อควรระวัง
                </h3>
                <ul className="text-sm text-red-800 space-y-1 ml-4">
                  <li>• ต้องตั้งค่าทั้ง Cloudflare (ฝั่ง EduMi) และ Domain Provider (ฝั่งผู้สอน)</li>
                  <li>• Proxy status ต้องเป็น <span className="font-semibold">Proxied</span> เพื่อให้ใช้ SSL ของ Cloudflare</li>
                  <li>• ถ้าผู้สอนใช้ root domain (ไม่มี www) ให้แนะนำใช้ CNAME Flattening หรือ ALIAS Record</li>
                </ul>
              </div>

              {/* Close Button */}
              <button
                onClick={() => setShowInfoModal(false)}
                className="w-full px-6 py-3 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-xl hover:from-blue-700 hover:to-purple-700 transition-all font-semibold"
              >
                เข้าใจแล้ว
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
